import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { isPast } from 'date-fns';

interface OverdueItem {
  id: string;
  sales_order_id: string;
  quantity: number;
  promised_date: string;
  sales_order: {
    order_number: string;
    customer_name: string;
  };
  manufactured_product: {
    product_name: string;
  };
}

const WeOweAlertBanner: React.FC = () => {
  const [overdueItems, setOverdueItems] = useState<OverdueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    loadOverdueItems();

    const dismissedUntil = localStorage.getItem('weOweBannerDismissed');
    if (dismissedUntil) {
      const dismissedTime = new Date(dismissedUntil);
      if (dismissedTime > new Date()) {
        setDismissed(true);
      } else {
        localStorage.removeItem('weOweBannerDismissed');
      }
    }
  }, []);

  const loadOverdueItems = async () => {
    try {
      const { data, error } = await supabase
        .from('we_owe_items')
        .select(`
          id,
          sales_order_id,
          quantity,
          promised_date,
          sales_order:sales_orders(order_number, customer_name),
          manufactured_product:manufactured_products(product_name)
        `)
        .eq('status', 'pending')
        .not('promised_date', 'is', null)
        .order('promised_date', { ascending: true });

      if (error) throw error;

      const overdue = (data || []).filter(item =>
        item.promised_date && isPast(new Date(item.promised_date))
      );

      setOverdueItems(overdue);
    } catch (error) {
      console.error('Error loading overdue We Owe items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    const dismissUntil = new Date();
    dismissUntil.setHours(dismissUntil.getHours() + 4);
    localStorage.setItem('weOweBannerDismissed', dismissUntil.toISOString());
    setDismissed(true);
  };

  if (loading || dismissed || overdueItems.length === 0) {
    return null;
  }

  const mostCritical = overdueItems[0];

  return (
    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg shadow-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-6 w-6 text-red-600" />
        </div>
        <div className="ml-3 flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-semibold text-red-800">
                {overdueItems.length} Overdue We Owe {overdueItems.length === 1 ? 'Item' : 'Items'}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-1">
                  <span className="font-medium">Most Critical:</span>{' '}
                  {mostCritical.manufactured_product?.product_name} × {mostCritical.quantity}
                </p>
                <p className="text-xs">
                  Customer: {mostCritical.sales_order?.customer_name} |
                  Order #{mostCritical.sales_order?.order_number}
                </p>
              </div>
              <div className="mt-3">
                <Link
                  to={`/sales-orders/${mostCritical.sales_order_id}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-red-800 hover:text-red-900 underline"
                >
                  View Order Details
                  <ExternalLink className="h-4 w-4" />
                </Link>
                <span className="mx-2 text-red-400">|</span>
                <Link
                  to="/sales-orders?filter=we-owe"
                  className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                >
                  View All We Owe Items
                </Link>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 ml-4 text-red-500 hover:text-red-700 transition-colors"
              title="Dismiss for 4 hours"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeOweAlertBanner;
